import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FolderOpen, ChevronRight } from 'lucide-react';
import { ProfileCard, ProfileCardIcon, ProfileCardStatus } from '@/components/profile/ProfileCard';
import { ProfileCardSkeleton } from '@/components/profile/ProfileCardSkeleton';
import { getVideosWithPendingResources } from '@/services/data/userAssets';
import { useAuthStore } from '@/stores/authStore';
import { AnimatePresence } from 'framer-motion';

interface UserAssetsSectionProps {
  readonly delay?: number;
  readonly isLoading?: boolean;
}

export function UserAssetsSection({ delay = 0, isLoading: externalLoading }: UserAssetsSectionProps) {
  const navigate = useNavigate();
  const { userId } = useAuthStore();
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [internalLoading, setInternalLoading] = useState(true);

  useEffect(() => {
    const checkPendingResources = async () => {
      if (!userId) return;
      setInternalLoading(true);
      try {
        const pending = await getVideosWithPendingResources(userId);
        setPendingCount(pending.length);
      } catch (error) {
        console.error('Error checking pending resources:', error);
        setPendingCount(0);
      } finally {
        setInternalLoading(false);
      }
    };
    checkPendingResources();
  }, [userId]);

  const hasPending = pendingCount !== null && pendingCount > 0;
  const isComplete = pendingCount === 0;
  
  // Use external loading state if provided, otherwise use internal
  const isLoading = externalLoading ?? internalLoading;

  return (
    <AnimatePresence mode="wait">
      {isLoading ? (
        <ProfileCardSkeleton type="simple" key="resources-skeleton" />
      ) : (
        <ProfileCard 
          key="resources-card"
          delay={delay} 
          onClick={() => navigate('/app/user-assets')}
          className="group"
        >
          <div className="p-6 flex items-center justify-between w-full">
            <div className="flex items-center gap-4">
              <ProfileCardIcon 
                icon={<FolderOpen />} 
                isComplete={isComplete}
                isPending={hasPending}
                showCompletionBadge={isComplete}
              />
              <div>
                <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Ressources</h3>
                <div className="mt-1">
                  <ProfileCardStatus 
                    isComplete={isComplete}
                    completeText="Tout est à jour"
                    incompleteText={`${pendingCount} vidéo${pendingCount && pendingCount > 1 ? 's' : ''} en attente`}
                  />
                </div>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
          </div>
        </ProfileCard>
      )}
    </AnimatePresence>
  );
}
